import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Eye, TrendingDown, Trash2, Edit } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { formatPrice } from '../utils/formatters';
import PriceCountdown from '../components/listing/PriceCountdown';
import { listingService } from '../services/listingService';

export default function MyListings() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    listingService.getBySeller(user.id)
      .then(setListings)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  if (!user) return (
    <div className="max-w-xl mx-auto px-4 py-20 text-center">
      <p className="text-xl font-semibold text-apple-gray-dark mb-4">{t('my_listings.need_login')}</p>
      <Link to="/login" className="btn-primary">{t('login.submit')}</Link>
    </div>
  );

  const limitUsed  = listings.length;
  const limitTotal = user.listingLimit === undefined || user.listingLimit === null ? '∞' : user.listingLimit;

  const handleDelete = async (id) => {
    if (!window.confirm(t('my_listings.delete_confirm'))) return;
    try {
      await listingService.delete(id);
      setListings(prev => prev.filter(l => l.id !== id));
    } catch (err) {
      alert(err?.response?.data?.message || t('my_listings.delete_error'));
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-apple-gray-dark">{t('my_listings.title')}</h1>
          <p className="text-sm text-apple-gray-mid mt-1">
            {limitUsed} / {limitTotal} {t('my_listings.used_limit')}
            {typeof limitTotal === 'number' && (
              <span className="ml-2 text-xs">
                <span className="inline-block w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden align-middle">
                  <span className="block h-full bg-apple-blue rounded-full" style={{ width: `${Math.min((limitUsed / limitTotal) * 100, 100)}%` }} />
                </span>
              </span>
            )}
          </p>
        </div>
        <Link to="/create" className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" /> {t('my_listings.new_listing')}
        </Link>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => <div key={i} className="card p-5 h-32 animate-pulse bg-apple-gray" />)}
        </div>
      ) : listings.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-5xl mb-4">📦</p>
          <h3 className="text-xl font-semibold text-apple-gray-dark mb-2">{t('my_listings.no_listings')}</h3>
          <p className="text-apple-gray-mid mb-6">{t('my_listings.create_first')}</p>
          <Link to="/create" className="btn-primary">{t('header.post_listing')}</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {listings.map(listing => {
            const expired = listing.expiresAt
              ? new Date(listing.expiresAt).getTime() <= Date.now()
              : false;
            return (
            <div key={listing.id} className={`card p-5 ${expired ? 'opacity-70' : ''}`}>
              <div className="flex gap-4">
                <div className="w-24 h-24 rounded-xl overflow-hidden bg-apple-gray shrink-0">
                  {listing.images?.[0]
                    ? <img src={listing.images[0].url || listing.images[0]} alt={listing.title} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-apple-gray-mid text-xs">—</div>
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <Link to={`/listing/${listing.id}`} className="font-semibold text-apple-gray-dark hover:text-apple-blue transition-colors line-clamp-1">
                      {listing.title}
                    </Link>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${expired ? 'bg-gray-100 text-gray-500' : listing.isReserved ? 'bg-orange-100 text-orange-600' : 'bg-green-100 text-green-700'}`}>
                      {expired ? t('price_countdown.expired_short') : listing.isReserved ? t('my_listings.status_reserved') : t('my_listings.status_active')}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 mt-2 text-xs text-apple-gray-mid">
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {listing.viewCount || 0}</span>
                    <span className="flex items-center gap-1"><TrendingDown className="w-3 h-3 text-apple-red" /> {formatPrice(listing.dropAmount)} / {listing.dropIntervalHours}s</span>
                    <span>#{listing.listingNo}</span>
                  </div>

                  <div className="mt-2">
                    <PriceCountdown listing={listing} compact />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                <Link to={`/listing/${listing.id}`} className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1">
                  <Eye className="w-3 h-3" /> {t('my_listings.views')}
                </Link>
                {expired ? (
                  <span className="text-xs px-3 py-1.5 rounded-full border border-gray-200 text-gray-400 flex items-center gap-1 cursor-not-allowed select-none">
                    <Edit className="w-3 h-3" /> {t('my_listings.edit')}
                  </span>
                ) : (
                  <Link to={`/edit/${listing.id}`} className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1">
                    <Edit className="w-3 h-3" /> {t('my_listings.edit')}
                  </Link>
                )}
                <button
                  onClick={() => handleDelete(listing.id)}
                  className="text-xs px-3 py-1.5 rounded-full border border-red-200 text-apple-red hover:bg-red-50 transition-colors flex items-center gap-1 ml-auto"
                >
                  <Trash2 className="w-3 h-3" /> {t('my_listings.delete')}
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
